/**
 * IcsTransferDetailView — VB6-style read-only form for an ICS warehouse transfer.
 * Mirrors the VB6 transfer detail form layout: labeled rows for header info,
 * a Complete checkbox, items grid (#, Item No., Item Name, Qty Req., Qty Ship, Unit, Wgt Ship),
 * expandable weight sub-rows, and a Total Weight display.
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Download,
  Package,
  Printer,
  Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { inventoryService, WarehouseTransferDetail, WarehouseTransfer } from '../../../services/inventoryService';
import { HteTransfer, HteTransferDetail } from '../../../services/hteTransferService';
import FieldBox from './FieldBox';

/** Union type for transfer data - supports both ICS and HTE transfers */
type TransferData = WarehouseTransfer | HteTransfer;
type TransferDetailData = WarehouseTransferDetail | HteTransferDetail;

interface Props {
  /** Transfer number - required for ICS transfers (will fetch data) */
  transferNumber?: string;
  /** Pre-loaded transfer data - when provided, skips fetching (for HTE transfers) */
  transferData?: TransferData;
  onBack?: () => void;
  onGoToTransfer?: (transferNumber: string) => void;
  /** Callback for "Receive Transfer" button - only shown for pending transfers when provided */
  onReceiveTransfer?: () => void;
  /** Hide action buttons (Print, Download PDF, Complete) when embedded in a parent that provides its own */
  hideActions?: boolean;
}

const IcsTransferDetailView = ({ transferNumber, transferData, onBack, onGoToTransfer, onReceiveTransfer, hideActions }: Props) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'A' || user?.role === 'admin';
  // Managers should not see the Receive Transfer button (that action is for supervisors/operators)
  const isManager = user?.role === 'M' || user?.role === 'manager';
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [quickSearchValue, setQuickSearchValue] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [printingPdf, setPrintingPdf] = useState(false);

  // Only fetch if transferData is not provided and transferNumber exists
  const { data: fetchedTransfer, isLoading } = useQuery(
    ['ics-transfer-detail', transferNumber],
    () => inventoryService.getWarehouseTransfer(transferNumber!),
    { enabled: !transferData && !!transferNumber }
  );

  // Fetch location and warehouse code lookups for display names
  const { data: locationCodes } = useQuery(
    'location-codes',
    () => inventoryService.getLocationCodes(),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: warehouseCodes } = useQuery(
    'warehouse-codes',
    () => inventoryService.getWarehouseCodes(),
    { staleTime: 5 * 60 * 1000 }
  );

  // Build lookup maps: code → description
  const locationMap = useMemo(() => {
    if (!locationCodes) return new Map<string, string>();
    return new Map(locationCodes.map(l => [l.location_code, l.description]));
  }, [locationCodes]);
  const warehouseMap = useMemo(() => {
    if (!warehouseCodes) return new Map<string, string>();
    return new Map(warehouseCodes.map(w => [w.warehouse_code, w.description]));
  }, [warehouseCodes]);

  // Format "CODE — Description" for display, falls back to just the code
  const formatLocation = useCallback((code?: string) => {
    if (!code) return '';
    const desc = locationMap.get(code);
    return desc ? `${code} — ${desc}` : code;
  }, [locationMap]);
  const formatWarehouse = useCallback((code?: string) => {
    if (!code) return '';
    const desc = warehouseMap.get(code);
    return desc ? `${code} (${desc})` : code;
  }, [warehouseMap]);

  // Use provided transferData or fetched data
  const transfer = transferData || fetchedTransfer;

  const toggleRow = (detailId: number) => {
    setExpandedRowId(prev => prev === detailId ? null : detailId);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr || dateStr.length !== 8) return dateStr || '';
    return `${dateStr.slice(4, 6)}/${dateStr.slice(6, 8)}/${dateStr.slice(0, 4)}`;
  };

  const formatDateTime = (dateStr?: string, timeStr?: string) => {
    if (!dateStr || dateStr.length !== 8) return '';
    const date = `${dateStr.slice(4, 6)}/${dateStr.slice(6, 8)}/${dateStr.slice(0, 4)}`;
    if (!timeStr || timeStr.length < 6) return date;
    return `${date} ${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}`;
  };

  // WCO-2507: weight reflects ACTUAL ship data only — scanned per-box weights if present,
  // else the recorded weight_shipped (which the VB6 ship-out scan sets; 0 before the scan).
  // Do NOT fall back to unit_weight × quantity_shipped: that fabricates an estimated weight
  // and, now that creation no longer seeds weight_shipped/weight_info, it would render as a
  // captured value before anything is shipped. Empty (0.00) before the scan is correct.
  const getEffectiveWeight = (detail: TransferDetailData): number => {
    if (detail.weights && detail.weights.length > 0) {
      return detail.weights.reduce((sum, w) => sum + Number(w.weight), 0);
    }
    return Number(detail.weight_shipped) || 0;
  };

  // WCO-2632: the UOM to display for a detail row. A sub-unit line (use_sub_unit='Y', e.g. a
  // not-intact remainder measured in LB/BG) must show the item's sub-unit, not the case unit (CS).
  // Prefer the backend-resolved unit; fall back to computing it client-side (item_sub_unit for a
  // sub-unit line, else item_unit) for HTE-transfer data / older responses that lack resolved_unit.
  const unitOf = (detail: TransferDetailData): string => {
    const d = detail as WarehouseTransferDetail;
    if (d.resolved_unit) return d.resolved_unit;
    return d.use_sub_unit === 'Y'
      ? (d.item_sub_unit || 'PC')
      : (d.item_unit || '');
  };

  // Helper to get item name - handles both ICS (item_name) and HTE (item_english_name) fields
  const getItemName = (detail: TransferDetailData): string => {
    return (detail as WarehouseTransferDetail).item_name
      || (detail as HteTransferDetail).item_english_name
      || '-';
  };

  // Helper to get Chinese name - handles both ICS (chinese_name) and HTE (item_chinese_name) fields
  const getChineseName = (detail: TransferDetailData): string | undefined => {
    return (detail as WarehouseTransferDetail).chinese_name
      || (detail as HteTransferDetail).item_chinese_name;
  };

  // Aggregate duplicate rows into single display rows (sums qty/weight, merges weights arrays).
  // WCO-2643: key by item_number AND use_sub_unit so an INTACT case line (use_sub_unit='N') and a
  // NOT-INTACT piece line (use_sub_unit='Y') of the SAME raw material stay SEPARATE — they are two
  // distinct warehouse_transfer_detail rows (different units, CS vs PC) and must not be summed onto
  // one line. Keying by item_number alone merged them (qty 4+1=5, weight 200+7.80=207.80, unit PC).
  const aggregatedDetails = useMemo(() => {
    if (!transfer?.details) return [];
    const map = new Map<string, TransferDetailData>();
    for (const d of transfer.details) {
      const aggKey = `${d.item_number}::${(d as WarehouseTransferDetail).use_sub_unit || ''}`;
      const existing = map.get(aggKey);
      if (existing) {
        (existing as any).quantity_request = (Number(existing.quantity_request) || 0) + (Number(d.quantity_request) || 0);
        (existing as any).quantity_shipped = (Number(existing.quantity_shipped) || 0) + (Number(d.quantity_shipped) || 0);
        (existing as any).weight_shipped = (Number(existing.weight_shipped) || 0) + (Number(d.weight_shipped) || 0);
        if (d.weights && d.weights.length > 0) {
          (existing as any).weights = [...(existing.weights || []), ...d.weights];
        }
      } else {
        map.set(aggKey, { ...d, weights: d.weights ? [...d.weights] : [] } as TransferDetailData);
      }
    }
    return Array.from(map.values());
  }, [transfer]);

  const totalWeight = useMemo(() => {
    if (!aggregatedDetails.length) return 0;
    return aggregatedDetails.reduce((sum, d) => sum + getEffectiveWeight(d), 0);
  }, [aggregatedDetails]);

  // Find the currently expanded detail row for the right-side weight panel
  const expandedDetail = aggregatedDetails.find((d: TransferDetailData) => d.id === expandedRowId) || null;

  // Get transfer number from either prop or transfer data
  const effectiveTransferNumber = transferNumber || transfer?.transfer_number;

  const handleDownloadPdf = async () => {
    if (!effectiveTransferNumber) return;
    try {
      setDownloadingPdf(true);
      const blob = await inventoryService.downloadIcsTransferPdf(effectiveTransferNumber);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${effectiveTransferNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('warehouseTransfer.pdfDownloadError'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrintPdf = async () => {
    if (!effectiveTransferNumber) return;
    try {
      setPrintingPdf(true);
      const blob = await inventoryService.downloadIcsTransferPdf(effectiveTransferNumber);
      const url = window.URL.createObjectURL(blob);
      // Open PDF in new window and trigger print
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('warehouseTransfer.pdfDownloadError'));
    } finally {
      setPrintingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        {t('common.loading')}
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        Transfer not found
      </div>
    );
  }

  const isComplete = transfer.is_complete === 'Y';
  const isDraftTransfer = 'status' in transfer && transfer.status === 'draft';
  const isCancelledTransfer = 'status' in transfer && transfer.status === 'cancelled';
  // For HTE transfers, check status field; for ICS transfers, pending means not complete
  const isPending = 'status' in transfer
    ? transfer.status === 'pending'
    : transfer.is_complete !== 'Y';

  return (
    <div className="space-y-5 overflow-x-hidden">
      {/* Back button + quick search — only shown when onBack is provided */}
      {(onBack || onGoToTransfer) && (
        <div className="hidden md:flex flex-wrap gap-y-2 items-center justify-between">
          {onBack ? (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('warehouseTransfer.backToResults')}
            </button>
          ) : <div />}
          {onGoToTransfer && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const val = quickSearchValue.trim();
              if (val && onGoToTransfer) {
                onGoToTransfer(val);
                setQuickSearchValue('');
              }
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={quickSearchValue}
              onChange={(e) => setQuickSearchValue(e.target.value)}
              placeholder={t('warehouseTransfer.searchByTransferNo')}
              className="w-28 sm:w-40 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="submit"
              disabled={!quickSearchValue.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md shadow-sm transition-colors"
            >
              <Search className="w-4 h-4" />
              {t('warehouseTransfer.searchIcs')}
            </button>
          </form>
          )}
        </div>
      )}

      {/* Header section */}
      <div className="bg-white dark:bg-gray-800 md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-700">
        {/* Title bar: Transfer #, then Operator + datetime below */}
        <div className="flex items-stretch gap-0 border-b border-gray-100 dark:border-gray-700">
          {/* Left: transfer info */}
          <div className="flex-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2.5 sm:py-3">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                  {t('warehouseTransfer.transferNumber')}
                </h2>
                <span className="font-mono text-base font-semibold text-blue-600 dark:text-blue-400 tracking-wide">
                  {transfer.transfer_number}
                </span>
              </div>
              <div className="flex items-center gap-5 text-sm text-gray-500 dark:text-gray-400">
                <span>
                  {t('warehouseTransfer.operator')}:
                  <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">{transfer.operator_id || '-'}</span>
                </span>
                {/* HTE transfers show updated_at, ICS transfers show m_date/m_time */}
                {'updated_at' in transfer && transfer.updated_at ? (
                  <span className="text-gray-900 dark:text-white">{new Date(transfer.updated_at).toLocaleString()}</span>
                ) : 'm_date' in transfer && transfer.m_date ? (
                  <span className="text-gray-900 dark:text-white">{formatDateTime(transfer.m_date, (transfer as WarehouseTransfer).m_time)}</span>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!hideActions && (
                <>
                  {/* Receive Transfer button - desktop only (mobile uses square button on right)
                      Hidden for managers - they should not receive transfers */}
                  {isPending && onReceiveTransfer && !isManager && (
                    <button
                      onClick={onReceiveTransfer}
                      className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm transition-colors"
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      {t('warehouseTransfer.receiveTransfer')}
                    </button>
                  )}
                  {/* TBHTE-383: Print/Download hidden for manager/supervisor, visible for admin only */}
                  {isAdmin && (
                    <>
                      <button
                        onClick={handlePrintPdf}
                        disabled={printingPdf}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t('warehouseTransfer.printPdf')}
                      >
                        <Printer className="w-4 h-4" />
                        {printingPdf ? t('common.loading') : t('warehouseTransfer.print')}
                      </button>
                      <button
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t('warehouseTransfer.downloadPdf')}
                      >
                        <Download className="w-4 h-4" />
                        {downloadingPdf ? t('common.loading') : t('warehouseTransfer.downloadPdf')}
                      </button>
                    </>
                  )}
                </>
              )}
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                  isComplete
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                    : isCancelledTransfer
                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      : isDraftTransfer
                        ? 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
                        : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800'
                }`}
              >
                {isComplete
                  ? <CheckCircle2 className="w-3.5 h-3.5" />
                  : <Circle className="w-3.5 h-3.5" />
                }
                {isComplete ? t('warehouseTransfer.completeCheck') : isCancelledTransfer ? t('status.cancelled') || 'Cancelled' : isDraftTransfer ? 'Draft' : 'Pending'}
              </span>
            </div>
          </div>
          {/* Right: big square Receive button on mobile only
              Hidden for managers - they should not receive transfers */}
          {!hideActions && isPending && onReceiveTransfer && !isManager && (
            <button
              onClick={onReceiveTransfer}
              className="md:hidden flex flex-col items-center justify-center gap-1.5 w-24 min-h-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white transition-colors"
            >
              <ClipboardCheck className="w-7 h-7" />
              <span className="text-xs font-semibold leading-tight text-center">
                {t('warehouseTransfer.receive') || 'Receive'}
              </span>
            </button>
          )}
        </div>

        {/* Mobile: compact card layout */}
        <div className="md:hidden px-3 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.requestDate')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(transfer.request_date)}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.transferDate')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(transfer.transfer_date)}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.requestBy')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{transfer.request_by || '-'}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.receiveBy')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{transfer.receive_by || '-'}</p>
            </div>
          </div>
          {transfer.memo && (
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.memo')}</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">{transfer.memo}</p>
            </div>
          )}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-2 grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.from')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatLocation(transfer.location_code_from) || formatWarehouse(transfer.warehouse_code_from) || '-'}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('warehouseTransfer.to')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatWarehouse(transfer.warehouse_code_to) || formatLocation(transfer.location_code_to) || '-'}</p>
            </div>
          </div>
        </div>

        {/* Desktop: form grid layout */}
        <div className="hidden md:block px-5 py-4">
          <div className="grid grid-cols-[130px_1fr_130px_1fr] items-center gap-x-3 gap-y-2.5">
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.requestDate')}</span>
            <FieldBox value={formatDate(transfer.request_date)} />
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.requestBy')}</span>
            <FieldBox value={transfer.request_by || ''} />

            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.transferDate')}</span>
            <FieldBox value={formatDate(transfer.transfer_date)} />
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.receiveBy')}</span>
            <FieldBox value={transfer.receive_by || ''} />

            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.memo')}</span>
            <div className="col-span-3">
              <FieldBox value={transfer.memo || ''} className="w-full block" />
            </div>

            <div className="col-span-4 border-t border-gray-100 dark:border-gray-700 my-0.5" />

            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.warehouseFrom')}</span>
            <FieldBox value={formatWarehouse(transfer.warehouse_code_from)} />
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.locationFrom')}</span>
            <FieldBox value={formatLocation(transfer.location_code_from)} />

            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.warehouseTo')}</span>
            <FieldBox value={formatWarehouse(transfer.warehouse_code_to)} />
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('warehouseTransfer.locationTo')}</span>
            <FieldBox value={formatLocation(transfer.location_code_to)} />
          </div>
        </div>
      </div>

      {/* Transfer Detail + Total Weight */}
      <div className="space-y-4 md:space-y-0 min-w-0">
        {/* Desktop: side-by-side layout. Mobile: stacked with Total Weight full-width on top */}
        <div className="flex flex-col md:flex-row gap-4 items-start min-w-0">

          {/* Transfer Detail table */}
          <div className="w-full md:flex-1 min-w-0 md:bg-white md:dark:bg-gray-800 md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-3 sm:px-5 py-2 sm:py-2.5 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                {t('warehouseTransfer.icsTransferDetails')}
              </h3>
            </div>
            {(!aggregatedDetails || aggregatedDetails.length === 0) ? (
              <div className="p-10 text-center text-gray-400 dark:text-gray-500">
                <Package className="mx-auto w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">{t('warehouseTransfer.noItems')}</p>
              </div>
            ) : (
              <>
              {/* Mobile: card layout */}
              <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                {aggregatedDetails.map((detail: TransferDetailData, idx: number) => {
                  const chineseName = getChineseName(detail);
                  const hasWeights = detail.weights && detail.weights.length > 0;
                  const isExpanded = expandedRowId === detail.id;
                  return (
                    <div
                      key={detail.id ?? `${detail.item_number}-${idx}`}
                      className={`px-3 py-2.5 ${hasWeights ? 'cursor-pointer active:bg-gray-50 dark:active:bg-gray-700' : ''}`}
                      onClick={() => hasWeights && toggleRow(detail.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium">{idx + 1}.</span>
                            <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">{detail.item_number}</span>
                            {detail.is_frozen === 'Y' && <span className="text-[10px] text-blue-500">*</span>}
                            {hasWeights && (
                              isExpanded
                                ? <ChevronDown className="w-3 h-3 text-blue-500" />
                                : <ChevronRight className="w-3 h-3 text-gray-400" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {chineseName && <span>{chineseName} </span>}
                            {getItemName(detail)}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">{Number(detail.quantity_request || 0).toFixed((detail as WarehouseTransferDetail).use_sub_unit === 'Y' ? 2 : 0)} <span className="text-sm font-medium text-gray-500">{unitOf(detail) || 'cs'}</span></p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{getEffectiveWeight(detail).toFixed(2)} lbs</p>
                        </div>
                      </div>
                      {/* Expanded weight info */}
                      {isExpanded && hasWeights && (
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <div className="grid grid-cols-4 gap-1">
                            {detail.weights!.map((w: any, wi: number) => (
                              <div key={wi} className="text-center py-1 bg-gray-50 dark:bg-gray-700/50 rounded text-xs font-mono text-gray-700 dark:text-gray-300">
                                {Number(w.weight).toFixed(2)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Desktop: full table */}
              <div className="hidden md:block overflow-x-auto scrollbar-visible">
                <table className="w-max min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      {/* Fixed columns - always visible */}
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">#</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                        {t('warehouseTransfer.itemNumber')}
                      </th>
                      <th className="px-3 py-2.5 w-8"></th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-56 whitespace-nowrap">
                        {t('warehouseTransfer.itemName')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.quantityRequest')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                        {t('warehouseTransfer.quantityShipped')}
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                        {t('warehouseTransfer.unit')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                        {t('warehouseTransfer.weightShipped')}
                      </th>
                      {/* Additional scrollable columns */}
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                        {t('warehouseTransfer.remark')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.dateCode')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                        {t('warehouseTransfer.specialRequest')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.avgSold')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.avgBack')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.qsType')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.duration')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                        {t('warehouseTransfer.scanComplete')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.qsPend')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                        {t('warehouseTransfer.qsRecNumber')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                        {t('warehouseTransfer.salespersonQs1')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                        {t('warehouseTransfer.qItem')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregatedDetails.map((detail: TransferDetailData, idx: number) => {
                      const hasWeights = detail.weights && detail.weights.length > 0;
                      const isExpanded = expandedRowId === detail.id;
                      const chineseName = getChineseName(detail);
                      return (
                        <tr
                          key={detail.id ?? `${detail.item_number}-${idx}`}
                          className={`
                            border-b border-gray-100 dark:border-gray-700/50
                            ${hasWeights ? 'cursor-pointer' : ''}
                            ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                            hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-colors
                          `}
                          onClick={() => hasWeights && toggleRow(detail.id)}
                        >
                          {/* Fixed columns */}
                          <td className="px-3 py-2.5 text-center text-sm text-gray-400 dark:text-gray-500 w-12">
                            <div className="flex items-center justify-center gap-1">
                              <span>{idx + 1}</span>
                              {hasWeights && (
                                isExpanded
                                  ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                                  : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                            {detail.item_number}
                          </td>
                          <td className="px-1 py-2.5 text-center text-sm text-gray-500 dark:text-gray-400 w-8">
                            {detail.is_frozen === 'Y' ? '*' : '+'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 w-56 whitespace-nowrap">
                            {chineseName && (
                              <span className="text-gray-500 dark:text-gray-400">{chineseName} </span>
                            )}
                            {getItemName(detail)}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-24 whitespace-nowrap">
                            {detail.quantity_request != null ? Number(detail.quantity_request).toFixed(2) : ''}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                            {detail.quantity_shipped != null ? Number(detail.quantity_shipped).toFixed(2) : ''}
                          </td>
                          <td className="px-3 py-2.5 text-center text-sm text-gray-600 dark:text-gray-300 w-16">
                            {unitOf(detail) || ''}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-32 whitespace-nowrap">
                            {getEffectiveWeight(detail).toFixed(2)}
                          </td>
                          {/* Additional scrollable columns */}
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                            {detail.remark || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.date_code || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                            {detail.special_request || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.avg_sold != null ? Number(detail.avg_sold).toFixed(2) : '-'}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.avg_back != null ? Number(detail.avg_back).toFixed(2) : '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.qs_type || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.duration || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                            {detail.scan_complete || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.qs_pend || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                            {detail.qs_rec_number || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                            {detail.salesperson_qs1 || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                            {detail.q_item || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          {/* Desktop-only: Right column with Total Weight + Weight Info panel */}
          <div className="hidden md:block shrink-0 w-44 space-y-3">
            {/* Total Weight box */}
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-800 shadow-sm border-l-4 border-l-green-500 dark:border-l-green-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                {t('warehouseTransfer.totalWeightLabel')}
              </div>
              <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400 text-right tabular-nums">
                {totalWeight.toFixed(2)}
              </div>
            </div>

            {/* Weight info list for the selected row */}
            {expandedDetail && expandedDetail.weights && expandedDetail.weights.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                {/* Header with item number */}
                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 font-mono">
                    {expandedDetail.item_number}
                  </div>
                </div>
                {/* Column header */}
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 w-10">#</th>
                      <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {t('warehouseTransfer.weightLb')}
                      </th>
                    </tr>
                  </thead>
                </table>
                {/* Scrollable weight rows */}
                <div className="max-h-[192px] overflow-y-auto">
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                      {expandedDetail.weights.map((w, wIdx) => (
                        <tr key={'weight_id' in w ? w.weight_id : w.id} className="bg-white dark:bg-gray-800">
                          <td className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 w-10 select-none">{wIdx + 1}</td>
                          <td className="px-3 py-1 text-xs text-right font-mono text-gray-900 dark:text-gray-100">
                            {Number(w.weight).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Total row */}
                <div className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-3 py-1.5 flex justify-between">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Total</span>
                  <span className="text-xs font-medium font-mono text-gray-900 dark:text-gray-100">
                    {expandedDetail.weights.reduce((sum, w) => sum + Number(w.weight), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile-only: Total Weight full-width at the bottom */}
        <div className="w-full md:hidden border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-800 shadow-sm border-l-4 border-l-green-500 dark:border-l-green-600">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('warehouseTransfer.totalWeightLabel')}
            </div>
            <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400 tabular-nums">
              {totalWeight.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IcsTransferDetailView;
